<script lang="ts">
  import type { Attachment } from 'svelte/attachments'
  import heroPosterMobile from '$lib/assets/branding/hero-poster-mobile.webp'
  import heroPoster from '$lib/assets/branding/hero-poster.webp'
  import Button from '$lib/components/shadcn/button/button.svelte'
  import ButtonArrowUpSvg from '../../components/SVGs/ButtonArrowUpSVG.svelte'
  import LaunchButton from '../../components/LaunchButton.svelte'

  const assetOrigin = 'https://assets.butane.dev'
  const desktopWebm = `${assetOrigin}/hero-v3-desktop.webm`
  const desktopMp4 = `${assetOrigin}/hero-v3-desktop.mp4`
  const mobileWebm = `${assetOrigin}/hero-v3-mobile.webm`
  const mobileMp4 = `${assetOrigin}/hero-v3-mobile.mp4`

  let isVideoReady = $state(false)

  const managePlayback: Attachment<HTMLVideoElement> = (video) => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let isIntersecting = false
    const markLoading = () => (isVideoReady = false)
    const markReady = () => {
      if (!reducedMotion.matches) isVideoReady = true
    }
    const playWhenVisible = () => {
      if (isIntersecting && !document.hidden && !reducedMotion.matches) void video.play().catch(() => {})
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        isIntersecting = entry?.isIntersecting ?? false
        if (isIntersecting) playWhenVisible()
        else video.pause()
      },
      { threshold: 0.05 },
    )
    const handleVisibilityChange = () => {
      if (document.hidden) video.pause()
      else playWhenVisible()
    }
    const syncMotionPreference = () => {
      if (reducedMotion.matches) {
        observer.unobserve(video)
        video.pause()
        markLoading()
        return
      }

      observer.observe(video)
      video.load()
      if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) markReady()
      playWhenVisible()
    }

    video.addEventListener('canplay', markReady)
    video.addEventListener('loadstart', markLoading)
    video.addEventListener('emptied', markLoading)
    video.addEventListener('error', markLoading)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    reducedMotion.addEventListener('change', syncMotionPreference)
    syncMotionPreference()

    return () => {
      observer.disconnect()
      video.removeEventListener('canplay', markReady)
      video.removeEventListener('loadstart', markLoading)
      video.removeEventListener('emptied', markLoading)
      video.removeEventListener('error', markLoading)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      reducedMotion.removeEventListener('change', syncMotionPreference)
    }
  }
</script>

<svelte:head>
  <link rel="preconnect" href={assetOrigin} />
  <link
    rel="preload"
    href={heroPosterMobile}
    as="image"
    type="image/webp"
    media="(max-width: 767px)"
    fetchpriority="high"
  />
  <link
    rel="preload"
    href={heroPoster}
    as="image"
    type="image/webp"
    media="(min-width: 768px)"
    fetchpriority="high"
  />
</svelte:head>

<div class="mx-auto">
  <section class="relative overflow-hidden">
    <div
      class="pointer-events-none absolute top-[0%] z-[0] h-0 w-full p-0 lg-mobile:top-[8%] sm-desktop:top-[12%]"
    >
      <picture>
        <source media="(max-width: 767px)" srcset={heroPosterMobile} />
        <img
          class="relative top-[-150px] mx-auto h-[1100px] w-full scale-125 object-contain sm-desktop:scale-100"
          src={heroPoster}
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchpriority="high"
        />
      </picture>
      <video
        {@attach managePlayback}
        class="absolute left-0 top-[-150px] mx-auto h-[1100px] w-full scale-125 object-contain transition-opacity duration-500 motion-reduce:hidden sm-desktop:scale-100 {isVideoReady
          ? 'opacity-100'
          : 'opacity-0'}"
        playsinline
        autoplay
        loop
        muted
        defaultMuted
        disableremoteplayback
        preload="auto"
        aria-hidden="true"
        tabindex="-1"
      >
        <source
          src={mobileWebm}
          type="video/webm"
          media="(max-width: 767px) and (prefers-reduced-motion: no-preference)"
        />
        <source
          src={mobileMp4}
          type="video/mp4"
          media="(max-width: 767px) and (prefers-reduced-motion: no-preference)"
        />
        <source
          src={desktopWebm}
          type="video/webm"
          media="(min-width: 768px) and (prefers-reduced-motion: no-preference)"
        />
        <source
          src={desktopMp4}
          type="video/mp4"
          media="(min-width: 768px) and (prefers-reduced-motion: no-preference)"
        />
      </video>
    </div>

    <div class="relative z-[1] flex flex-col items-center">
      <div class="mt-36 h-[283px] max-w-[706.52px] self-center">
        <h1
          class="w-[90vw] text-center font-cirka text-[10vw] font-medium leading-[90%] sm:w-[500px] sm:text-[65px] sm:leading-[60px] sm-desktop:w-[706.52px] sm-desktop:text-[76px] sm-desktop:leading-[66px]"
        >
          The gateway to a secure and diverse synthetic market
        </h1>
        <LaunchButton />
      </div>
      <div class="mt-[50vw] flex h-[283px] max-w-[406.52px] flex-col sm-desktop:mt-[40%]">
        <p
          class="w-[80vw] self-center text-center font-graphik text-[5vw] font-normal sm:w-[520px] sm:text-[26px] sm:leading-[35px]"
        >
          Butane is a fast, powerful, and innovative decentralised synthetics platform built on the Cardano blockchain.
        </p>

        <Button
          variant="ghost"
          href="https://files.butane.dev/white-paper.pdf"
          target="_blank"
          class="mx-auto mt-[26px] flex h-[46px] w-[150px] items-center justify-evenly rounded-[100px] bg-white text-black shadow-sm shadow-[#939EB8] transition-all duration-300 hover:scale-[1.01] hover:bg-white/80 hover:text-black active:scale-[.99]"
        >
          <p class="mr-2 text-center font-graphik text-sm font-normal">WHITEPAPER</p>
          <ButtonArrowUpSvg svgStyles="h-[30px] w-[30px]" fillStyles="black" />
        </Button>
      </div>
    </div>
  </section>
</div>

<style lang="postcss">
  :global(.button-gradient) {
    background: var(--Button, linear-gradient(118deg, #24caff 39.09%, #fdb1a8 76.37%, #ffa564 93.38%));
    @apply text-black;
  }
</style>
